// Copyright (c) Bentley Systems
import { useCallback, useEffect, useState } from "react";
import { AppDispatch } from "../app/store";
import { ConfigManager } from "../Helpers/config-manager";
import { setErrorState } from "../Helpers/helper-functions";
import { ClientTelemetryOverview, DayFilterValue, OnPremProjectKey, ProjectRunStatus } from "./Models/on-prem-telemetry-models";

type UseOnPremTelemetryOverviewApiProps = {
  accessToken: string;
  dispatch: AppDispatch;
  isNodeAllowed: boolean;
  /** Only runs last run within this many days are fetched; 0 (default) fetches every retained run. */
  dayFilter?: DayFilterValue;
};

/** Shape of one project-run row as returned by the `overview` endpoint (camelCase wire contract). */
type OverviewProjectRunResponse = {
  projectId: string;
  projectName?: string;
  iTwinId: string;
  lastRunSuccess?: boolean;
  lastRun: string;
  isMuted?: boolean;
};

/** Shape of one client row as returned by the `overview` endpoint (camelCase wire contract). */
type OverviewClientResponse = {
  mqttClientName: string;
  installedVersion?: string;
  lastUsed?: string;
  trackedProjectCount?: number;
  projects?: OverviewProjectRunResponse[];
};

export const useOnPremTelemetryOverviewApi = ({
  accessToken,
  dispatch,
  isNodeAllowed,
  dayFilter = 0,
}: UseOnPremTelemetryOverviewApiProps) => {
  const [clients, setClients] = useState<ClientTelemetryOverview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);

  const mapClients = (payload: unknown): ClientTelemetryOverview[] => {
    const rows: OverviewClientResponse[] = Array.isArray(payload) ? payload : [];

    return rows.map((row) => ({
      mqttClientName: row.mqttClientName,
      installedVersion: row.installedVersion,
      lastUsed: row.lastUsed,
      trackedProjectCount: row.trackedProjectCount,
      projects: (row.projects ?? []).map(
        (p): ProjectRunStatus => ({
          projectId: p.projectId,
          projectName: p.projectName,
          iTwinId: p.iTwinId,
          lastRunSuccess: p.lastRunSuccess ?? false,
          lastRun: p.lastRun,
          isMuted: p.isMuted ?? false,
        }),
      ),
    }));
  };

  const fetchOverview = useCallback(
    async (signal?: AbortSignal) => {
      setIsLoading(true);
      const runId = crypto.randomUUID();
      const url = `${ConfigManager.projectProviderUrl}/api/OnPremTelemetry/overview?days=${dayFilter}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal,
        });

        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }

        const data = await response.json();
        setClients(mapClients(data));
      } catch (error: unknown) {
        if ((error as { name?: string })?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, dispatch, dayFilter],
  );

  const setProjectsMuted = useCallback(
    async (keys: OnPremProjectKey[], muted: boolean): Promise<boolean> => {
      if (keys.length === 0) {
        return false;
      }
      setIsMutating(true);
      const runId = crypto.randomUUID();
      const url = `${ConfigManager.projectProviderUrl}/api/OnPremTelemetry/projects/mute`;

      try {
        const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          body: JSON.stringify({ keys, muted }),
        });

        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return false;
        }

        await fetchOverview();
        return true;
      } catch {
        setErrorState(500, dispatch, runId, true);
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [accessToken, dispatch, fetchOverview],
  );

  const deleteProjectRuns = useCallback(
    async (keys: OnPremProjectKey[]): Promise<boolean> => {
      if (keys.length === 0) {
        return false;
      }
      setIsMutating(true);
      const runId = crypto.randomUUID();
      const url = `${ConfigManager.projectProviderUrl}/api/OnPremTelemetry/projects/delete`;

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          body: JSON.stringify(keys),
        });

        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return false;
        }

        await fetchOverview();
        return true;
      } catch {
        setErrorState(500, dispatch, runId, true);
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [accessToken, dispatch, fetchOverview],
  );

  useEffect(() => {
    if (!isNodeAllowed) {
      return;
    }
    const abortController = new AbortController();
    void fetchOverview(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [fetchOverview, isNodeAllowed]);

  return {
    clients,
    isLoading,
    isMutating,
    fetchOverview,
    setProjectsMuted,
    deleteProjectRuns,
  };
};
